import {
    KeyboardEvent,
    ClipboardEvent,
    useState, Dispatch,
} from 'react';
import { forgotPassword } from '../../dal/data-provider';
import { IServerValidation } from '../../../../domain/validation/interfaces';
import ValidationErrors from '../../../../domain/validation/server-errors/validation-errors';
import {
    createControl,
    updateControl,
    validateForm,
    removeInvisibleCharsOnPaste,
    validateControl
} from '../../../../domain/validation/validation';
import { getSimpleCaptchaValidationError } from '../../../../domain/captcha/simple-captcha';
import { getIsChina, getIsAuthenticated } from '../../../../domain/hidden-data-provider';
import { getRecaptchaToken, renderRecaptcha2, resetRecaptcha2, getRecaptcha2Error } from '../../../../domain/captcha/recaptcha-provider';
import Recaptcha3Text from '../../../../domain/captcha/recaptcha3-terms';
import LogoutScreen from '../../logout/logout-screen';
import { IFormControl } from '../../../../ui/forms/interfaces';
import FormTextField from '../../../../ui/forms/fields/text-field';
import ChinaCaptchaField from '../../../../ui/forms/fields/captcha-china-field';
import FormTitle from '../../../../ui/forms/form-title';
import FormBorder from '../../../../ui/forms/form-border';
import FormPreloader from '../../../../ui/forms/form-preloader';
import RadioTabs from '../ui/radio-tabs';
import { rootActions, useAppDispatch, useAppSelector } from '../data/store';
import SuccessMessage from '../ui/success-msg';
import ErrorMessage from '../ui/error-msg';

/**
 * Forgot Password
 */
const ForgotPasswordApp = () => {

    const dispatch = useAppDispatch();

    const isChina = getIsChina();
    const isUserAuth = getIsAuthenticated();
    const isUserTabSelected = useAppSelector(store => store.root.isUserTabSelected);
    const error = useAppSelector(store => store.root.error);

    // validation ---------------------------------------
    const userNameControl = createControl('userName', '', {
        required: true
    });
    const emailControl = createControl('email', '', {
        required: true,
        email: true,
    });
    const simpleCaptchaControl = createControl('simpleCaptcha', '', {
        required: true,
        requiredMessage: getSimpleCaptchaValidationError(),
        length: 5,
    });

    const [isLoading, setIsLoading] = useState(false);
    const [response, setResponse] = useState<IServerValidation|null>();
    const [userName, setUserName] = useState<IFormControl>(userNameControl);
    const [email, setEmail] = useState<IFormControl>(emailControl);

    // simple captcha - used for chine
    const [simpleCaptchaCode, setSimpleCaptchaCode] = useState<IFormControl>(simpleCaptchaControl);
    const [simpleCaptchaAttempts, setSimpleCaptchaAttempts] = useState(0);

    // google recaptcha version 2 & 3
    const [recaptchaVersion, setRecaptchaVersion] = useState(3);
    const [recaptcha2WidgetID, setRecaptcha2WidgetID] = useState(0);

    // captcha: common
    const [captchaError, setCaptchaError] = useState('');

    /*
    useEffect(() =>{
        setRecaptchaVersion(2);
        renderCaptcha2Fallback();
    }, []);
     */

    /**
     * if recaptcha 3 returns low score (the limit is defined in kentico settings),
     * then recaptcha 2 fallback should be rendered inside <div id="recaptcha"></div>
     */
    const renderCaptcha2Fallback = () => {
        if(!window.grecaptcha) return;

        window.grecaptcha.ready(async () => {
            const widgetID = await renderRecaptcha2('recaptcha');
            setRecaptcha2WidgetID(widgetID);
        });
    };

    /**
     * perform user login on server
     */
    const requestNewPassword = async () => {

        dispatch(
            rootActions.main({
                isSuccess: false,
                error: '',
            })
        );

        const params: ([IFormControl, Dispatch<any>])[] = [];

        if(isChina){
            params.push([simpleCaptchaCode, setSimpleCaptchaCode]);
        }

        if(isUserTabSelected){
            params.push([userName, setUserName]);
        }
        else{
            params.push([email, setEmail]);
        }

        // client side validation ----------
        const isValid = validateForm(params);

        if(!isValid) return;

        setIsLoading(true);

        let response = null;

        try {
            const captchaToken = !isChina ? await getRecaptchaToken(recaptchaVersion) : simpleCaptchaCode.value;

            if(!captchaToken){
                setCaptchaError(isChina ? getSimpleCaptchaValidationError() : getRecaptcha2Error());
                setIsLoading(false);
                dispatch(
                    rootActions.main({
                        isSuccess: false,
                        error: '',
                    })
                );
                return;
            }

            response = await forgotPassword(
                isUserTabSelected ? userName.value : email.value,
                !isUserTabSelected,
                captchaToken,
                recaptchaVersion
            );
        }
        catch(ex){}

        setIsLoading(false);

        if(
            response &&
            response.statusCode === 200 &&
            response.data &&
            response.data.isValid &&
            response.data.relatedData &&
            response.data.relatedData.errorCode === 0
        ){

            // all is well ---------------
            dispatch(
                rootActions.main({
                    isSuccess: true,
                    error: '',
                })
            );

            setCaptchaError('');
            return;
        }

        // handle errors ---------
        if(isChina){

            // re-render simple captcha
            setSimpleCaptchaAttempts(simpleCaptchaAttempts + 1);
        }
        else{
            if(response?.data.relatedData === 'recaptcha-error') {
                setRecaptchaVersion(2);
                renderCaptcha2Fallback();
            }

            if(recaptchaVersion === 2){
                resetRecaptcha2();
            }
        }

        // render errors
        setResponse(response?.data);

        dispatch(
            rootActions.main({
                isSuccess: false,
                error: response?.data?.relatedData?.errorMessage ?? 'An error has occurred.',
            })
        );
    };

    /**
     * and enter / return keys
     */
    const handleEnter = async (evt: KeyboardEvent<HTMLInputElement>) => {
        if(evt.key === 'Enter') {
            await requestNewPassword();
        }
    };

    /**
     * empty errors on click
     */
    const handleFieldClick = () =>{
        setResponse(null);
        setCaptchaError('');
    };

    return (
        <>
            {
                !isUserAuth &&
                <FormBorder>
                    <FormTitle title={ 'Reset your Waves password' } />

                    <p className="mb-2">Enter your username or email address:</p>

                    <RadioTabs />

                    {
                        isUserTabSelected &&
                        <FormTextField
                            id={ 'username-tb' }
                            label={ 'Username' }
                            control={ userName }
                            maxLength={ 100 }
                            qa={ 'forgot-password-username' }
                            onChange={ evt => {
                                const $target = evt.target as HTMLInputElement;
                                setUserName(updateControl(userName, $target.value, !userName.isValid));
                                setResponse(null); // empty server error
                                setCaptchaError('');
                            }}
                            onBlur={ () => {
                                setUserName(validateControl(userName));
                            }}
                            onClick={ handleFieldClick }
                            onKeyUp={ handleEnter }
                            onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                                setUserName(updateControl(userName, removeInvisibleCharsOnPaste(evt)));
                            }}
                        />
                    }

                    {
                        !isUserTabSelected &&
                        <FormTextField
                            id={ 'email-tb' }
                            label={ 'Email' }
                            control={ email }
                            maxLength={ 80 }
                            onChange={ evt => {
                                const $target = evt.target as HTMLInputElement;
                                setEmail(updateControl(email, $target.value, !email.isValid));
                                setResponse(null); // empty server error
                                setCaptchaError('');
                            }}
                            onBlur={ () => {
                                setEmail(validateControl(email));
                            }}
                            onClick={ handleFieldClick }
                            onKeyUp={ handleEnter }
                            onPaste={ (evt: ClipboardEvent<HTMLInputElement>) => {
                                setEmail(updateControl(email, removeInvisibleCharsOnPaste(evt)));
                            }}
                        />
                    }

                    {
                        /* ------------- google recaptcha version 2 or 3 ------------- */
                        !isChina &&
                        <div id="recaptcha" className={`mb-4 flex justify-center recaptcha recaptcha-version-${ recaptchaVersion }`}/>
                    }

                    {
                        /* ------------- china simple captcha ------------- */
                        isChina &&
                        <ChinaCaptchaField
                            control={ simpleCaptchaCode }
                            setSimpleCaptchaCode={ setSimpleCaptchaCode }
                            simpleCaptchaAttempts={ simpleCaptchaAttempts }
                        />
                    }

                    <div className="mb-2">

                        { isLoading && <FormPreloader /> }

                        {
                            !isLoading &&
                            <button
                                className="w-btn"
                                type="button"
                                data-qa="forgot-password-button"
                                onClick={async () => {
                                    await requestNewPassword();
                                }}
                            >Request New Password</button>
                        }
                    </div>

                    {
                        response && !response.isValid && (
                            <div className="text-sm text-red my-4 text-center" role="alert">
                                <ValidationErrors errors={ response.errors } />
                            </div>
                        )
                    }

                    { error && <ErrorMessage text={ error } /> }
                    <SuccessMessage />

                    {
                        captchaError &&
                        <div className="text-sm text-red my-4 text-center" data-id={ recaptcha2WidgetID } role="alert">{ captchaError }</div>
                    }

                    {
                        !isChina && recaptchaVersion === 3 &&
                        <div className="mt-8 text-center">
                            <Recaptcha3Text />
                        </div>
                    }

                </FormBorder>
            }

            { isUserAuth && <LogoutScreen text="You're already logged in. To reset your password, please sign out first." /> }
        </>

    )
};

export default ForgotPasswordApp;